import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { X, Camera, Zap, AlertCircle, Maximize, Keyboard, RefreshCw, Smartphone } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BrowserQrScanner = ({ onScan, onClose, title = "СЕРВИС СКАНИРОВАНИЯ" }) => {
    const scannerRef = useRef(null);
    const html5QrCodeRef = useRef(null);
    const [isStarted, setIsStarted] = useState(false);
    const [error, setError] = useState(null);
    const [isTorchOn, setIsTorchOn] = useState(false);
    const [cameras, setCameras] = useState([]);
    const [activeCameraId, setActiveCameraId] = useState(null);
    const [diagnostics, setDiagnostics] = useState({ resolution: 'N/A', fps: 'N/A' });

    const startScanner = useCallback(async (cameraId) => {
        if (html5QrCodeRef.current) {
            try {
                if (html5QrCodeRef.current.isScanning) {
                    await html5QrCodeRef.current.stop();
                }

                const config = {
                    fps: 30, 
                    qrbox: (viewfinderWidth, viewfinderHeight) => {
                        const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
                        const size = Math.floor(minEdge * 0.75);
                        return { width: size, height: size };
                    },
                    aspectRatio: 1.0,
                    formatsToSupport: [
                        Html5QrcodeSupportedFormats.QR_CODE,
                        Html5QrcodeSupportedFormats.CODE_128,
                        Html5QrcodeSupportedFormats.CODE_39,
                        Html5QrcodeSupportedFormats.EAN_13,
                        Html5QrcodeSupportedFormats.UPC_A
                    ],
                    experimentalFeatures: {
                        useBarCodeDetectorIfSupported: true
                    }
                };

                await html5QrCodeRef.current.start(
                    cameraId || { facingMode: "environment" },
                    config,
                    (decodedText) => {
                        if (window.navigator.vibrate) window.navigator.vibrate([100, 50, 100]);
                        onScan(decodedText);
                        stopScanner();
                    },
                    (errorMessage) => {
                        // Noise handling
                    }
                );
                
                setIsStarted(true);
                setError(null);
                
                const videoElement = document.querySelector('#reader video');
                if (videoElement && videoElement.srcObject) {
                    const track = videoElement.srcObject.getVideoTracks()[0];
                    const settings = track.getSettings();
                    setDiagnostics({
                        resolution: `${settings.width}x${settings.height}`,
                        fps: Math.round(settings.frameRate) || '30'
                    });
                }
            } catch (err) {
                console.error("Scanner start error:", err);
                setError("Доступ к камере отклонен или аппаратная ошибка. Проверьте разрешения.");
                toast.error("ОШИБКА КАМЕРЫ");
            }
        }
    }, [onScan]);

    const stopScanner = useCallback(async () => {
        if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
            try {
                await html5QrCodeRef.current.stop();
                setIsStarted(false);
            } catch (err) { console.error("Scanner stop error:", err); }
        }
    }, []);

    useEffect(() => {
        html5QrCodeRef.current = new Html5Qrcode("reader");

        const init = async () => {
            try {
                // First, try to get list of cameras to identify back cameras
                const devs = await Html5Qrcode.getCameras();
                setCameras(devs);
                
                if (devs && devs.length > 0) {
                    // Look for back cameras specifically
                    const backCameras = devs.filter(c => 
                        /back|rear|environment|основная|задняя|0/i.test(c.label)
                    );
                    
                    // If we found back cameras, use the first one. Otherwise use environment constraint.
                    if (backCameras.length > 0) {
                        const targetId = backCameras[0].id;
                        setActiveCameraId(targetId);
                        await startScanner(targetId);
                    } else {
                        // Fallback to general environment constraint if labels are missing or don't match
                        await startScanner({ facingMode: "environment" });
                    }
                } else {
                    // No devices listed (permissions?), try environment constraint
                    await startScanner({ facingMode: "environment" });
                }
            } catch (e) {
                console.warn("Scanner init warning (trying environment):", e);
                await startScanner({ facingMode: "environment" });
            }
        };

        const timeoutId = setTimeout(init, 500); 

        return () => {
            clearTimeout(timeoutId);
            stopScanner();
        };
    }, [startScanner, stopScanner]);

    const toggleTorch = async () => {
        if (html5QrCodeRef.current && isStarted) {
            try {
                const state = !isTorchOn;
                await html5QrCodeRef.current.applyVideoConstraints({
                    advanced: [{ torch: state }]
                });
                setIsTorchOn(state);
            } catch (e) {
                toast.error("ВСПЫШКА НЕ ПОДДЕРЖИВАЕТСЯ");
            }
        }
    };

    const switchCamera = () => {
        if (cameras.length < 2) return;
        const currentIndex = cameras.findIndex(c => c.id === activeCameraId);
        const nextIndex = (currentIndex + 1) % cameras.length;
        const nextId = cameras[nextIndex].id;
        setActiveCameraId(nextId);
        startScanner(nextId);
    };

    const handleManualCode = () => {
        const code = prompt("ВВЕДИТЕ КОД БИЛЕТА ВРУЧНУЮ:");
        if (code) onScan(code.trim());
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black flex flex-col items-center justify-between pointer-events-auto animate-in fade-in duration-500 font-sans">
            {/* Header / StatusBar */}
            <div className="w-full p-8 flex items-center justify-between bg-neutral-900 shadow-2xl safe-top border-b border-white/5">
                <div className="flex items-center gap-5">
                    <div className="w-12 h-12 rounded-[1rem] bg-indigo-600 flex items-center justify-center text-white shadow-lg active:scale-95 shadow-indigo-600/20">
                        <Smartphone size={24} />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-[11px] font-black uppercase tracking-[0.2em] text-white italic">{title}</span>
                        <div className="flex items-center gap-3">
                            <span className="text-[8px] font-black text-emerald-500 uppercase flex items-center gap-1">
                                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" /> СИСТЕМА ОНЛАЙН
                            </span>
                            <span className="text-[8px] font-black text-zinc-600 uppercase">РАЗР: {diagnostics.resolution}</span>
                        </div>
                    </div>
                </div>
                <button 
                    onClick={onClose} 
                    className="w-14 h-14 rounded-[1.2rem] bg-zinc-800 flex items-center justify-center text-white/50 hover:text-white transition-all active:scale-90"
                >
                    <X size={28} />
                </button>
            </div>

            {/* Scanner Area */}
            <div className="relative flex-1 w-full flex items-center justify-center overflow-hidden bg-[#050505]">
                <div id="reader" className="w-full h-full grayscale-[0.5] contrast-125"></div>
                
                {/* HUD Overlay */}
                <div className="absolute inset-x-0 top-12 flex justify-center pointer-events-none z-10">
                    <div className="bg-black/60 backdrop-blur-xl px-6 py-2 rounded-full border border-white/10 flex items-center gap-3">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full animate-ping" />
                        <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Ожидание сигнала...</span>
                    </div>
                </div>

                <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
                    {/* Professional Viewfinder */}
                    <div className="w-72 h-72 border border-white/10 rounded-[3rem] relative bg-indigo-500/[0.03]">
                        <div className="absolute top-0 left-0 w-12 h-12 border-t-[6px] border-l-[6px] border-indigo-500 rounded-tl-[2.5rem] shadow-[0_0_15px_rgba(99,102,241,0.5)]"></div>
                        <div className="absolute top-0 right-0 w-12 h-12 border-t-[6px] border-r-[6px] border-indigo-500 rounded-tr-[2.5rem] shadow-[0_0_15px_rgba(99,102,241,0.5)]"></div>
                        <div className="absolute bottom-0 left-0 w-12 h-12 border-b-[6px] border-l-[6px] border-indigo-500 rounded-bl-[2.5rem] shadow-[0_0_15px_rgba(99,102,241,0.5)]"></div>
                        <div className="absolute bottom-0 right-0 w-12 h-12 border-b-[6px] border-r-[6px] border-indigo-500 rounded-br-[2.5rem] shadow-[0_0_15px_rgba(99,102,241,0.5)]"></div>
                        
                        <div className="absolute inset-x-4 top-0 h-[3px] bg-indigo-400 shadow-[0_0_20px_rgba(99,102,241,1)] animate-scan-fast opacity-80"></div>
                        
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 opacity-20">
                            <div className="absolute top-1/2 inset-x-0 h-px bg-white" />
                            <div className="absolute left-1/2 inset-y-0 w-px bg-white" />
                        </div>
                    </div>
                    
                    <p className="mt-16 text-zinc-500 text-[10px] font-black uppercase tracking-[0.4em] px-8 text-center leading-loose">
                        Наведите камеру <br/> на код билета
                    </p>
                </div>

                {error && (
                    <div className="absolute inset-0 z-50 bg-black/95 flex items-center justify-center p-10 text-center backdrop-blur-3xl">
                        <div className="space-y-8 max-w-[300px]">
                            <div className="w-20 h-20 bg-rose-600/10 rounded-[2rem] flex items-center justify-center mx-auto text-rose-600 border border-rose-600/20 shadow-2xl">
                                <AlertCircle size={40} strokeWidth={3} />
                            </div>
                            <div className="space-y-2">
                                <h4 className="text-white font-black italic uppercase text-lg">Ошибка Системы</h4>
                                <p className="text-zinc-500 text-xs font-medium leading-relaxed">{error}</p>
                            </div>
                            <button 
                                onClick={() => startScanner(activeCameraId)}
                                className="w-full py-5 bg-white text-black font-black uppercase tracking-[0.2em] rounded-2xl shadow-2xl active:scale-95 transition-all text-[11px]"
                            >
                                Перезапустить
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Tactical Footer Controls */}
            <div className="w-full p-10 grid grid-cols-3 gap-4 bg-neutral-900 border-t border-white/5 safe-bottom shadow-[0_-20px_50px_rgba(0,0,0,0.5)]">
                <button 
                    onClick={toggleTorch}
                    className={`flex flex-col items-center justify-center gap-3 p-6 rounded-[2rem] transition-all duration-500 ${isTorchOn ? 'bg-indigo-600 text-white shadow-2xl shadow-indigo-600/40 translate-y-[-4px]' : 'bg-white/[0.03] text-zinc-600 border border-white/5'}`}
                >
                    <Zap size={24} className={isTorchOn ? 'fill-current' : ''} />
                    <span className="text-[9px] font-black uppercase tracking-widest italic">{isTorchOn ? 'АКТИВНО' : 'СВЕТ'}</span>
                </button>

                <button 
                    onClick={switchCamera}
                    disabled={cameras.length < 2}
                    className={`flex flex-col items-center justify-center gap-3 p-6 rounded-[2rem] transition-all border border-white/5 ${cameras.length < 2 ? 'opacity-20' : 'bg-white/[0.03] text-white hover:bg-white/[0.08] active:scale-90 shadow-xl'}`}
                >
                    <RefreshCw size={24} />
                    <span className="text-[9px] font-black uppercase tracking-widest italic">КАМЕРА</span>
                </button>

                <button 
                    onClick={handleManualCode}
                    className="flex flex-col items-center justify-center gap-3 p-6 rounded-[2rem] bg-white text-black font-black uppercase tracking-widest shadow-2xl active:scale-90 transition-all hover:translate-y-[-2px]"
                >
                    <Keyboard size={24} />
                    <span className="text-[9px]">ВВОД</span>
                </button>
            </div>

            <style>{`
                @keyframes scan-fast {
                    0% { top: 10%; opacity: 0; }
                    20% { opacity: 1; }
                    80% { opacity: 1; }
                    100% { top: 90%; opacity: 0; }
                }
                .animate-scan-fast {
                    animation: scan-fast 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
                }
                #reader video {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: cover !important;
                }
                #reader {
                    border: none !important;
                }
                .safe-top { padding-top: env(safe-area-inset-top, 32px); }
                .safe-bottom { padding-bottom: env(safe-area-inset-bottom, 32px); }
            `}</style>
        </div>
    );
};

export default BrowserQrScanner;
